<template>
  <div class="object" :key="updateCardKey">
    <div v-if="!selectedObject" class="no object-card">
      <span>{{ $t('SelectObjectMessage') }}</span>
    </div>

    <div v-if="selectedObject" class="object-info-container">
      <object-main-info
        :fields="fields"
        :identifier="cardObjectIdentifier"
        :identifier-label="cardObjectIdentifierLabel"
        :editing-data="false"
        :show-edit-button="false"
        :card-label="null"
        :height="cardHeight"
      />

      <workorder-specific-info
        v-if="selectedObjectIsWorkOrder"
        :workorder-geom="object ? object.geo_json : null"
        :workorder-objects="object ? object.objects : []"
      />

      <activity-specific-info
        v-if="selectedObjectIsActivity"
        :activity="object ? object : null"
      />

      <task-step-specific-info
        v-if="selectedObjectIsTask"
      />
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="true"
      color="#29BDF0"
      style="position: fixed;"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from '@/newComponents/_Organisms/ObjectMainInfo';
import WorkorderSpecificInfo from "@/newComponents/TasksManagement/WorkorderSpecificInfo";
import ActivitySpecificInfo from "@/newComponents/TasksManagement/ActivitySpecificInfo";
import TaskStepSpecificInfo from "@/newComponents/TasksManagement/TaskStepSpecificInfo";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";

export default {
  name: "SelectedObject",

  components: {
    LoadingSpinner,
    ObjectMainInfo,
    WorkorderSpecificInfo,
    ActivitySpecificInfo,
    TaskStepSpecificInfo,
  },

  props: {
    selectedObject: {
      type: Object,
    },

    selectedObjectType: {
      type: String,
    },
  },

  mixins: [activitiesTasksDataMixin],

  data() {
    return {
      loading: false,

      updateCardKey: 0,

      fields: [],
      object: null,

      workorderIncludes: "workspace,userCreator",
      activityIncludes: "fibercloudObject",
      tasksIncludes: "assignedUser",
    }
  },

  watch: {
    "selectedObject.uuid"() {
      if (this.selectedObject && this.selectedObjectType) {
        switch (this.selectedObjectType) {
          case 'workorder':
            this.fetchWorkOrderByUUID(this.selectedObject.uuid);
            break;
          case 'activity':
            this.fetchActivityByUUID(this.selectedObject.uuid);
            break;
          case 'task':
            this.fetchTaskByUUID(this.selectedObject.uuid);
            break;
          default:
            break;
        }
      }
    }
  },

  computed: {
    selectedObjectIsWorkOrder() {
      return this.selectedObjectType === 'workorder'
    },

    selectedObjectIsActivity() {
      return this.selectedObjectType === 'activity'
    },

    selectedObjectIsTask() {
      return this.selectedObjectType === 'task'
    },

    cardObjectIdentifier() {
      if (this.object) {
        if (this.selectedObjectIsWorkOrder) {
          return this.object.name;
        }
        if (this.selectedObjectIsActivity) {
          return this.object.title;
        }
        if (this.selectedObjectIsTask) {
          return this.object.title;
        }
      }
      return "";
    },

    cardObjectIdentifierLabel() {
      return this.$i18n.tc('Label', 1);
    },

    cardHeight() {
      return this.selectedObjectIsTask ? '30%' : '28%';
    },
  },

  methods: {
    ...mapActions([
      'getWorkOrderByUUID',
      'fetchActivity',
      'fetchTask',
      'fetchWorkOrderObjects',
    ]),

    async fetchWorkOrderByUUID(uuid) {
      try {
        this.loading = true;

        let object = await this.getWorkOrderByUUID({
          uuid: uuid,
          params: { include: this.workorderIncludes }
        });
        this.object = object;

        let workorderObjects = await this.fetchWorkOrderObjects({
          uuid: uuid,
        });
        this.object.objects = workorderObjects;

        this.getWorkOrderFields(object);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchActivityByUUID(uuid) {
      try {
        this.loading = true;
        let object = await this.fetchActivity({ activityUUID: uuid, includes: this.activityIncludes });
        this.object = object;
        this.getActivityFields(object);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchTaskByUUID(uuid) {
      try {
        this.loading = true;
        let object = await this.fetchTask({ taskUUID: uuid, includes: this.tasksIncludes });
        this.object = object;
        this.getTaskFields(object);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    getWorkOrderFields(object) {
      if (object !== null) {
        this.fields = [
          {
            icon: "assignment_turned_in",
            label: this.$i18n.t("State"),
            value: this.$i18n.t(object.state),
            size: "100%"
          },
          {
            icon: "location_on",
            label: this.$i18n.tc("Workspace", 1),
            value: object.workspace && object.workspace.label
              ? object.workspace.label
              : object.workspace_uuid,
            size: "100%"
          },
          {
            icon: "priority_high",
            label: this.$i18n.t("Priority"),
            value: this.$i18n.t(object.priority.label),
            size: "100%"
          },
          {
            icon: "person",
            label: this.$i18n.t("Creator"),
            value: object.creator.first_name + ' ' + object.creator.last_name,
            size: "100%"
          },
          {
            icon: "event",
            label: this.$i18n.t("StartDate"),
            value: object.start_date,
            size: "100%"
          },
          {
            icon: "event_busy",
            label: this.$i18n.t("EndDate"),
            value: object.end_date,
            size: "100%"
          },
        ]
      } else {
        this.fields = [];
      }
    },

    getActivityFields(object) {
      if (object !== null) {
        this.fields = [
          {
            icon: "subject",
            label: this.$i18n.t("Description"),
            value: object.description ? object.description : '-',
            size: "100%"
          },
          {
            icon: "assignment_turned_in",
            label: this.$i18n.t("Status"),
            value: this.$i18n.t(object.state),
            size: "100%"
          },
          {
            icon: "event",
            label: this.$i18n.t("DueDate"),
            value: object.due_date,
            size: "100%"
          },
          {
            icon: "layers",
            label: this.$i18n.tc("Layer", 1),
            value: this.getObjectTypeLabel(object.object_type),
            size: "100%"
          },
          {
            icon: "label",
            label: this.$i18n.tc("Object", 1),
            value: object.object.label ? object.object.label : object.object_uuid ,
            size: "100%"
          },
          {
            icon: "person",
            label: this.$i18n.t("Creator"),
            value: this.getUserName(object.user_creator_id) ? this.getUserName(object.user_creator_id) : object.user_creator_id,
            size: "100%"
          },
        ]
      } else {
        this.fields = [];
      }
    },

    getTaskFields(object) {
      if (object !== null) {
        this.fields = [
          {
            icon: "assignment_turned_in",
            label: this.$i18n.t("Status"),
            value: this.$i18n.t(object.state),
            size: "100%"
          },
          {
            icon: "subject",
            label: this.$i18n.t("Description"),
            value: object.description ? object.description : '-',
            size: "100%"
          },
          {
            icon: "person",
            label: this.$i18n.tc("Assignee", 1),
            value: this.getUserName(object.user_assignee_id) ? this.getUserName(object.user_assignee_id) : object.user_assignee_id,
            size: "100%"
          },
          {
            icon: "event",
            label: this.$i18n.t("ScheduledDate"),
            value: this.getDateTime(object.scheduled_date),
            size: "100%"
          },
          {
            icon: "mdi-cash",
            label: this.$i18n.t("Cost"),
            value: object.cost ? object.cost : '-',
            size: "100%"
          },
          {
            icon: "event",
            label: this.$i18n.t("Estimation"),
            value: object.estimation_date ? object.estimation_date : '-',
            size: "100%"
          },
          {
            icon: "mdi-account-check",
            label: this.$i18n.t("AutoApproval"),
            value: object.auto_approval ? this.$i18n.t('Yes') : this.$i18n.t('No'),
            size: "100%"
          },
        ]
      } else {
        this.fields = [];
      }
    },
  }
}
</script>

<style scoped>
.object {
  height: 100%;
  max-height: 100%;
  background-color: transparent;
  position: relative;
  border-radius: 8px;
}

.object-card {
  height: 100%;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.object-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  background-color: #fff;
}

.no.object-card > span {
  color: #29BDF0;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.object-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}
</style>


// WEBPACK FOOTER //
// src/newComponents/TasksManagement/SelectedObject.vue